import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { RiDeleteBinLine } from "react-icons/ri";
import { FaRegEdit } from "react-icons/fa";
import { FaRegEye } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import { deleteCreatorSession, getAllCreatorSessions } from "../../redux/actions/mindfulness";
import { CircularProgress } from "@mui/material";
import { handleSuccessMessage } from "../../redux/actions/success";

// Styled Components
const ManageSessionsContainer = styled.div`
  padding: 20px;
  background-color: #fff;
  font-family: Montserrat;

  margin: auto;
  margin-top: 90px;
  width: 80%;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #ff6b6b, #ee5a52);
  padding: 2.9375rem 2.1875rem;
  border-radius: 12px;
  color: white;
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  box-sizing: border-box;
  padding-top: 3.75rem;
  border-top: 1px solid rgba(237, 30, 36, 1);
  border-radius: 15px 15px 0px 0px;
  background: linear-gradient(
    180deg,
    rgba(255, 106.66, 110.96, 1) 39%,
    rgba(237, 30, 36, 1) 100%
  );
`;

const HeaderTitle = styled.div`
  h1 {
    font-size: 1.875rem;
    margin: 0;
    font-weight: 400;
  }
  p {
    font-size: 1rem;
    font-weight: 400;
    opacity: 0.9;
    margin: 5px 0 0;
  }
`;

const SessionsContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
`;

const SectionHeader = styled.div`
  margin-bottom: 20px;

  h2 {
    font-size: 18px;
    font-weight: 400;
    margin: 0 0 5px 0;
    color: #333;
  }

  p {
    font-size: 14px;
    color: #666;
    margin: 0;
  }
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SessionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #eee;
  /* cursor: pointer; */

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #fff;
  }
`;

const SessionContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;

  .session-title {
    font-weight: 400;
    color: #333;
    font-size: 16px;
  }

  .session-description {
    font-size: 14px;
    color: #666;
  }

  .session-details {
    display: flex;
    gap: 15px;
    margin-top: 10px;

    .detail {
      font-size: 14px;
      color: #666;

      span {
        font-weight: 400;
        color: #333;
      }
    }
  }

  .status-tag {
    display: inline-block;
    background-color: #dcfce7;
    color: #00a63e;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    margin-right: 8px;
  }

  .format-tag {
    display: inline-block;
    background-color: #eff6ff;
    color: #155dfc;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
  }
`;

const SessionActions = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.variant === "view"
      ? "#e8f5e9"
      : props.variant === "edit"
      ? "#e3f2fd"
      : "#ffebee"};
  color: ${(props) =>
    props.variant === "view"
      ? "#00A63E"
      : props.variant === "edit"
      ? "#155DFC"
      : "#E7000B"};
  border: none;
  cursor: pointer;
  font-size: 16px;
  background-color: #fff;

  &:hover {
    opacity: 0.9;
  }
`;

const LoaderWrapper = styled.div`
  margin-top: 25%;
  text-align: center;
`;

const ManageSessions = () => {
  const sessionsData =
    useSelector((state) => state?.mindfulness?.creatorSessions) ?? [];

  const [sessions, setSessions] = useState(sessionsData);
  const [isLoading, setIsLoading] = useState(true);
  const totalSessions = sessionsData.length;
  const activeSessions = sessionsData.filter(
    (session) => session.is_active,
  ).length;
  const inactiveSessions = sessionsData.filter(
    (session) => !session.is_active,
  ).length;

  const dispatch = useDispatch();

  // Function to handle delete action
  const handleDeleteSession = (sessionId) => {
    setSessions(sessions.filter((session) => session.id !== sessionId));
    dispatch(deleteCreatorSession(sessionId)).then(() =>
      handleSuccessMessage("Session Deleted")
    );
  };

  useEffect(async () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    await dispatch(getAllCreatorSessions());
    setIsLoading(false);
  }, []);

  useEffect(() => {
    setSessions(sessionsData);
  }, [sessionsData]);

  console.log("apiData", sessionsData);

  if (isLoading) {
    return (
      <LoaderWrapper>
        <CircularProgress sx={{ color: "#ED1E24" }} size={50} />
      </LoaderWrapper>
    );
  }

  return (
    <ManageSessionsContainer>
      {/* Header */}
      <Header>
        <HeaderTitle>
          <h1>Manage My Sessions</h1>
          <p>View, edit, and manage all your counseling sessions</p>
        </HeaderTitle>
      </Header>

      {/* All Sessions */}
      <SessionsContainer>
        <SectionHeader>
          <h2>All Sessions ({totalSessions})</h2>
          <p>
            {activeSessions} active • {inactiveSessions} inactive
          </p>
        </SectionHeader>

        <SessionList>
          {sessions.map((session) => {
            // Get conversation types and amounts from conversation_type_amount object
            const conversationTypes = Object.keys(
              session.conversation_type_amount || {},
            );
            const firstType = conversationTypes[0];
            const price = firstType
              ? session.conversation_type_amount[firstType]
              : 0;
            const earnings = price * session.total_bookings;

            return (
              <SessionItem key={session.id}>
                <SessionContent>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      alignItems: "center",
                    }}
                  >
                    <div className="session-title">{session.name}</div>
                    <span className="status-tag">
                      {session.is_active ? "Active" : "Inactive"}
                    </span>
                    <span className="format-tag">
                      {firstType
                        ? firstType.charAt(0).toUpperCase() + firstType.slice(1)
                        : "N/A"}
                    </span>
                  </div>
                  <div className="session-description">{session.objective}</div>
                  <div className="session-details">
                    <div className="detail">
                      <span>Category:</span> {session.category}
                    </div>
                    <div className="detail">
                      <span>Duration:</span> {session.duration} min
                    </div>
                    <div className="detail">
                      <span>Price:</span> ₹{price}
                    </div>
                    <div className="detail">
                      <span>Bookings:</span> {session.total_bookings}
                    </div>
                    <div className="detail">
                      <span>Earnings:</span> ₹{earnings.toLocaleString()}
                    </div>
                  </div>
                  {/* <div>
                    <span className="status-tag">{session.is_active ? 'Active' : 'Inactive'}</span>
                    <span className="format-tag">{firstType ? firstType.charAt(0).toUpperCase() + firstType.slice(1) : 'N/A'}</span>
                  </div> */}
                </SessionContent>
                <SessionActions>
                  {/* <ActionButton
                    variant="view"
                    onClick={() => console.log(`View session ${session.id}`)}
                  >
                    <FaRegEye />
                  </ActionButton> */}
                  {/* <ActionButton
                    variant="edit"
                    onClick={() => console.log(`Edit session ${session.id}`)}
                  >
                    <FaRegEdit />
                  </ActionButton> */}
                  <ActionButton
                    variant="delete"
                    onClick={() => handleDeleteSession(session.id)}
                  >
                    <RiDeleteBinLine />
                  </ActionButton>
                </SessionActions>
              </SessionItem>
            );
          })}
        </SessionList>
      </SessionsContainer>
    </ManageSessionsContainer>
  );
};

export default ManageSessions;
